"use client";

import type { MouseEvent } from "react";
import "@/styles/chunks/service.css";

export type PackageCardProps = {
  name?: string;
  description?: string;
  price?: string;
  timeline?: string;
  features?: string[];
  ctaLabel?: string;
  ctaHref?: string;
  isRecommended?: boolean;
  index?: number;
};

const FORM_SECTION_ID = "locHeroForm";

export const QUOTE_PACKAGE_SELECT_EVENT = "bytelogic:quote-package-select";

const scrollToServiceForm = (hash: string, packageName: string) => {
  if (typeof window === "undefined") return;

  if (window.location.hash !== hash) {
    window.history.pushState(null, "", hash);
  }

  // pushState does not fire hashchange; notify ScrollToHash listeners.
  window.dispatchEvent(new Event("hashchange"));
  window.dispatchEvent(
    new CustomEvent(QUOTE_PACKAGE_SELECT_EVENT, {
      detail: { name: packageName },
    })
  );

  const scroll = () => {
    document
      .getElementById(FORM_SECTION_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  window.requestAnimationFrame(scroll);
};

const PackageCard = ({
  name,
  description,
  price,
  timeline,
  features = [],
  ctaLabel,
  ctaHref = `#${FORM_SECTION_ID}`,
  isRecommended = false,
  index = 0,
}: PackageCardProps) => {
  const resolvedName = name?.trim() || `Package ${index + 1}`;
  const filteredFeatures = features.filter((feature) => feature?.trim());
  const resolvedCtaLabel = ctaLabel?.trim() || `Get ${resolvedName}`;

  const handleGoToForm = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    scrollToServiceForm(
      ctaHref.startsWith("#") ? ctaHref : `#${FORM_SECTION_ID}`,
      resolvedName
    );
  };

  return (
    <article
      className={`service-pkg-card card-hover${isRecommended ? " service-pkg-card--featured" : ""}`}
      onClick={handleGoToForm}
    >
      {isRecommended ? (
        <span className="service-pkg-tag">Recommended</span>
      ) : null}
      <h3 className="service-pkg-name">{resolvedName}</h3>
      {description?.trim() ? (
        <p className="service-pkg-for">{description.trim()}</p>
      ) : (
        <p className="service-pkg-for service-pkg-for--empty" aria-hidden="true">
          &nbsp;
        </p>
      )}
      {price?.trim() ? (
        <p className="service-pkg-price">
          <span className="service-pkg-from">From</span>
          <span className="service-pkg-amount">{price.trim()}</span>
        </p>
      ) : null}
      {timeline?.trim() ? (
        <p className="service-pkg-time">{timeline.trim()}</p>
      ) : null}
      {filteredFeatures.length > 0 ? (
        <ul className="service-pkg-list">
          {filteredFeatures.map((feature, featureIndex) => (
            <li key={`${feature}-${featureIndex}`}>{feature.trim()}</li>
          ))}
        </ul>
      ) : null}
      <a
        className="service-pkg-cta"
        href={ctaHref}
        data-package-name={resolvedName}
        onClick={handleGoToForm}
      >
        {resolvedCtaLabel}
      </a>
    </article>
  );
};

export default PackageCard;
